// TreasuryDealerDashboard.js — Thin orchestrator
// Imports useLpDealerData, renders StickyHeader, tab nav, delegates to tab components.
// URL-path-based tabs following existing pattern.

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft, CheckCircle, FileText, BarChart3, Activity,
  Eye, RefreshCw, Zap, Shield
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useLpDealerData from '../../hooks/useLpDealerData';
import StickyHeader from '../lp/StickyHeader';
import AlertCenter from '../lp/AlertCenter';
import QuotePanel from '../lp/QuotePanel';
import BlotterTab from '../lp/BlotterTab';
import SettlementDocTab from '../lp/SettlementDocTab';
import PositionLimitsTab from '../lp/PositionLimitsTab';
import AnalyticsTab from '../lp/AnalyticsTab';
import ComplianceAuditTab from '../lp/ComplianceAuditTab';
import AutoRulesTab from '../lp/AutoRulesTab';

// URL PATH TO TAB MAPPING
const getTabFromPath = (pathname) => {
  if (pathname.includes('/rules')) return 'auto_rules';
  if (pathname.includes('/compliance')) return 'compliance_audit';
  if (pathname.includes('/quotes')) return 'fx_quotes';
  if (pathname.includes('/settlement')) return 'settlement';
  if (pathname.includes('/blotter')) return 'blotter';
  if (pathname.includes('/positions')) return 'position_limits';
  if (pathname.includes('/analytics')) return 'analytics';
  if (pathname.includes('/audit')) return 'compliance_audit';
  return 'fx_quotes';
};

const TreasuryDealerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pairFilter, setPairFilter] = useState('all');

  const data = useLpDealerData();

  // Active tab from URL
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Tab config
  const tabs = [
    { id: 'fx_quotes', label: 'Foreign Exchange Quotes', path: '/bank/treasury', icon: ArrowRightLeft },
    { id: 'settlement', label: 'Settlement', path: '/bank/treasury/settlement', icon: CheckCircle },
    { id: 'blotter', label: 'Blotter', path: '/bank/treasury/blotter', icon: FileText },
    { id: 'position_limits', label: 'Positions', path: '/bank/treasury/positions', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', path: '/bank/treasury/analytics', icon: Activity },
    { id: 'auto_rules', label: 'Auto Rules', path: '/bank/treasury/rules', icon: Zap },
    { id: 'compliance_audit', label: 'Compliance & Audit', path: '/bank/treasury/compliance', icon: Shield },
  ];

  // Loading state
  if (data.loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-zinc-600 mt-4">Loading treasury dealer dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-50 min-h-screen flex flex-col">
      {/* Sticky Header */}
      <StickyHeader
        user={user}
        priceFeed={data.priceFeed}
        cbReference={data.cbReference}
        bookContext={data.bookContext}
        dealerLimits={data.dealerLimits}
        metrics={data.metrics}
        pairFilter={pairFilter}
        onPairFilterChange={setPairFilter}
      />

      {/* Tab Navigation */}
      <div className="flex gap-1 px-4 pt-3 pb-2 bg-white border-b border-zinc-200 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-none font-mono text-[10px] uppercase tracking-[0.12em] transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-500 hover:bg-zinc-100'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.id === 'fx_quotes' && data.liveRfqs.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-zinc-500 text-white text-[9px] rounded-none">
                {data.liveRfqs.length}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        <AlertCenter
          alerts={data.alerts}
          onDismiss={data.dismissAlert}
        />
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-4">
        {activeTab === 'fx_quotes' && (
          <QuotePanel
            rfqs={data.rfqs}
            quotes={data.quotes}
            counterparties={data.counterparties}
            priceFeed={data.priceFeed}
            cbReference={data.cbReference}
            bookContext={data.bookContext}
            competitiveContext={data.competitiveContext}
            dealerLimits={data.dealerLimits}
            pairFilter={pairFilter}
            onSubmitQuote={data.submitQuote}
            onAmendQuote={data.amendQuote}
            onWithdrawQuote={data.withdrawQuote}
            onDeclineRfq={data.declineRfq}
            onGetCounterparty={data.getCounterparty}
            onGetPreTradeChecks={data.getPreTradeChecks}
            onGetCompetitiveCtx={data.getCompetitiveCtx}
          />
        )}

        {activeTab === 'settlement' && (
          <SettlementDocTab
            trades={data.trades}
            counterparties={data.counterparties}
            rfqMap={data.rfqMap}
            onReleaseTrade={data.releaseTrade}
            onUpdateSettlement={data.updateSettlement}
          />
        )}

        {activeTab === 'blotter' && (
          <BlotterTab
            quotes={data.quotes}
            trades={data.trades}
            counterparties={data.counterparties}
          />
        )}

        {activeTab === 'position_limits' && (
          <PositionLimitsTab
            bookContext={data.bookContext}
            dealerLimits={data.dealerLimits}
            trades={data.trades}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab
            quotes={data.quotes}
            trades={data.trades}
            competitiveContext={data.competitiveContext}
            onExportRegulatory={data.exportRegulatory}
          />
        )}

        {activeTab === 'auto_rules' && (
          <AutoRulesTab
            autoRules={data.autoRules}
            onUpdateRules={data.setAutoRules}
          />
        )}

        {activeTab === 'compliance_audit' && (
          <ComplianceAuditTab
            auditLog={data.auditLog}
            quotes={data.quotes}
            onGetEvidencePack={data.getEvidencePack}
          />
        )}
      </div>
    </div>
  );
};

export default TreasuryDealerDashboard;
